<template>
  <div v-if="showAnyCartView" class="carts-module" data-cy="carts-module">
    <IngredientsCartModal
      v-if="showIngredientsCartModal"
      :type="cartType"
      :plants="plants"
      @closeModal="showIngredientsCartModal = false"
    />
    <ProductsCartModal
      v-if="showProductsCartModal"
      :type="cartType"
      :plants="plants"
      @closeModal="showProductsCartModal = false"
    />
    <div class="carts-module__container">
      <header class="carts-module__header" data-cy="carts-module-title">
        My Carts
      </header>
      <div class="carts-module__content">
        <div
          v-if="showProductsCartView && canModifyProductCarts"
          class="carts-module__cart"
        >
          <span class="carts-module__cart-text" data-cy="carts-module-cart-text"
            >Products:</span
          >
          <div class="carts-module__cart-actions">
            <BadgeContainer
              v-for="action in productCartActions"
              :key="action.subApplication"
              content="content"
              rounded="full"
              :theme-color="action.theme"
              size="large"
              :cutout-border="true"
              data-cy="badge-container"
            >
              <LoadingButton
                type="button"
                :theme-color="action.theme"
                :loading="!action.loaded"
                :disabled="action.count === 0"
                @click="action.onClick"
                :data-cy="`complete-product-order-${action.subApplication}`"
                >{{ action.name
                }}<font-awesome-icon icon="fa-solid fa-cart-shopping" />
              </LoadingButton>
              <template v-slot:content>{{ action.count }}</template>
            </BadgeContainer>
          </div>
        </div>
        <div
          v-if="showIngredientsCartView && canModifyIngredientCarts"
          class="carts-module__cart"
        >
          <span class="carts-module__cart-text" data-cy="carts-module-cart-text"
            >Ingredients:</span
          >
          <div class="carts-module__cart-actions">
            <BadgeContainer
              v-for="action in ingredientCartActions"
              :key="action.subApplication"
              content="content"
              rounded="full"
              :theme-color="action.theme"
              size="large"
              :cutout-border="true"
              data-cy="badge-container"
            >
              <LoadingButton
                type="button"
                :theme-color="action.theme"
                :loading="!action.loaded"
                :disabled="action.count === 0"
                @click="action.onClick"
                :data-cy="`complete-ingredient-order-${action.subApplication}`"
                >{{ action.name
                }}<font-awesome-icon icon="fa-solid fa-cart-shopping" />
              </LoadingButton>
              <template v-slot:content>{{ action.count }}</template>
            </BadgeContainer>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { onBeforeMount, ref, computed, watchEffect } from "vue";
import { useMainStore } from "@/stores/useMainStore";
import { SubApplicationType } from "@/Domain/SubApplicationType";
import { useScheduleAStore } from "@/stores/useScheduleAStore";
import { useScheduleBStore } from "@/stores/useScheduleBStore";
import { BadgeContainer } from "@progress/kendo-vue-indicators";
import LoadingButton from "@/shared/ui/inputs/LoadingButton.vue";
import IngredientsCartModal from "@/views/scheduleA/IngredientsCartModal.vue";
import ProductsCartModal from "@/views/scheduleB/ProductsCartModal.vue";
import type { IdAndName } from "@/Domain/IdAndName";

interface CartAction {
  name: string;
  count: number;
  subApplication: SubApplicationType;
  loaded: boolean;
  theme: string;
  onClick: () => void;
}

const props = defineProps<{
  canModifyIngredientCarts: boolean;
  canModifyProductCarts: boolean;
}>();

const main = useMainStore();
const scheduleAStore = useScheduleAStore();
const scheduleBStore = useScheduleBStore();

const plants = ref<IdAndName[]>([]);
const cartType = ref<SubApplicationType>(SubApplicationType.Add);
const showProductsCartModal = ref(false);
const showIngredientsCartModal = ref(false);
const productsInCartCountUpdated = ref(false);

const ingredientCartActions = ref([
  {
    name: "Add Ingredients",
    count: scheduleAStore.getCartCount(SubApplicationType.Add),
    subApplication: SubApplicationType.Add,
    loaded: false,
    theme: "success",
    onClick: () => {
      cartType.value = SubApplicationType.Add;
      showIngredientsCartModal.value = true;
    },
  },
  {
    name: "Delete Ingredients",
    count: scheduleAStore.getCartCount(SubApplicationType.Terminate),
    subApplication: SubApplicationType.Terminate,
    loaded: false,
    theme: "error",
    onClick: () => {
      cartType.value = SubApplicationType.Terminate;
      showIngredientsCartModal.value = true;
    },
  },
] as CartAction[]);

const productCartActions = ref([
  {
    name: "Add New Product",
    count: scheduleBStore.getCartCount(SubApplicationType.Add),
    subApplication: SubApplicationType.Add,
    loaded: false,
    theme: "success",
    onClick: () => {
      cartType.value = SubApplicationType.Add;
      showProductsCartModal.value = true;
    },
  },
  {
    name: "Copy Products",
    count: scheduleBStore.getCartCount(SubApplicationType.Copy),
    subApplication: SubApplicationType.Copy,
    loaded: false,
    theme: "primary",
    onClick: () => {
      cartType.value = SubApplicationType.Copy;
      showProductsCartModal.value = true;
    },
  },
  {
    name: "Terminate Products",
    count: scheduleBStore.getCartCount(SubApplicationType.Terminate),
    subApplication: SubApplicationType.Terminate,
    loaded: false,
    theme: "error",
    onClick: () => {
      cartType.value = SubApplicationType.Terminate;
      showProductsCartModal.value = true;
    },
  },
] as CartAction[]);

const showProductsCartView = computed(() => {
  return productCartActions.value.some((action) => action.count);
});

const showIngredientsCartView = computed(() => {
  return ingredientCartActions.value.some((action) => action.count);
});

const showAnyCartView = computed(() => {
  return showProductsCartView.value || showIngredientsCartView.value;
});

const getProductsInCartCountAsync = async function (subApplication: string) {
  return api.get(
    "VueGetAllMatchingProductsInCartSubApplicationAndUserLoginCount",
    {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      subApplication: subApplication,
    }
  );
};

const updateIngredientsInCartCountAsync = async function () {
  const addAction = ingredientCartActions.value.find(
    (action) => action.subApplication === SubApplicationType.Add
  )!;
  const terminateAction = ingredientCartActions.value.find(
    (action) => action.subApplication === SubApplicationType.Terminate
  )!;

  const cartPromise = api.get("VueGetIngCartCount", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
  });

  await handleResponsePromiseAsync(
    cartPromise,
    (data) => {
      scheduleAStore.setCartCount(addAction.subApplication, data.adds);
      addAction.loaded = true;

      scheduleAStore.setCartCount(terminateAction.subApplication, data.deletes);
      terminateAction.loaded = true;
    },
    "CartsModule",
    "updateIngredientsInCartCountAsync"
  );
};

const updateProductsInCartCountAsync = async function () {
  if (productsInCartCountUpdated.value) return;

  productsInCartCountUpdated.value = true;
  const promises = productCartActions.value.map(async (action) => {
    return handleResponsePromiseAsync(
      getProductsInCartCountAsync(action.subApplication),
      (data) => {
        scheduleBStore.setCartCount(action.subApplication, data);
        action.loaded = true;
      },
      "CartsModule",
      "updateProductsInCartCountAsync"
    );
  });
  await Promise.all(promises);
};

const updatePlantListAsync = async function () {
  return handleResponsePromiseAsync(
    api.get("GetAuthorizedCertifiedOrPendingOwns", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      certifiedOnly: true,
    }),
    (data) => {
      plants.value = data;
    },
    "CartsModule",
    "updatePlantListAsync"
  );
};

const onCartCountUpdate = function (
  type: SubApplicationType,
  count: number,
  actions: CartAction[]
) {
  const action = actions.find((action) => action.subApplication === type);
  if (action) {
    action.count = count;
  }
};

scheduleAStore.handleCartCountUpdate = (
  type: SubApplicationType,
  count: number
) => onCartCountUpdate(type, count, ingredientCartActions.value);

scheduleBStore.handleCartCountUpdate = (
  type: SubApplicationType,
  count: number
) => onCartCountUpdate(type, count, productCartActions.value);

watchEffect(() => {
  if (props.canModifyProductCarts) {
    updateProductsInCartCountAsync();
  }
});

watchEffect(() => {
  if (props.canModifyIngredientCarts) {
    updateIngredientsInCartCountAsync();
  }
});

onBeforeMount(async () => {
  if (props.canModifyProductCarts) {
    updateProductsInCartCountAsync();
  }
  if (props.canModifyIngredientCarts) {
    updateIngredientsInCartCountAsync();
  }
  await updatePlantListAsync();
});
</script>

<style scoped lang="scss">
.carts-module {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__container {
    border: 1px solid #e8e8e8;
    background: white;
  }

  &__header {
    padding: 8px 15px;
    font-size: 13px;
    background: #efefef;
    border-bottom: 1px solid #e8e8e8;
    position: relative;
  }

  &__content {
    padding: 15px;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__cart {
    display: flex;
    gap: 20px;
    align-items: center;
    color: #999;

    &:nth-child(n + 2) {
      border-top: 1px solid #ddd;
      padding-top: 20px;
    }

    &-actions {
      display: flex;
      flex-direction: row;
      gap: 10px;
      flex-wrap: wrap;
    }
  }
  &__cart-text {
    width: 70px;
  }
  :deep(.k-badge-container .k-button-text) {
    display: flex;
    align-items: center;
    gap: 10px;
  }
}
</style>
